import { RefreshCw, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import React, { useState, useMemo, useEffect } from 'react';

export const ReportView = ({ reportData, customFields, fetchingReport, selectedFields, sortConfig, onSort, lookupMaps = {} }) => {
    const resolveFieldId = (field) => {
        if (field === null || field === undefined) return '';
        if (typeof field === 'string' || typeof field === 'number') return String(field);

        let baseId = field.id ?? field.key ?? field.value ?? '';
        while (typeof baseId === 'object' && baseId !== null && baseId.id !== undefined) {
            baseId = baseId.id;
        }
        return String(baseId);
    };

    const getFieldKey = (field) => {
        if (!field) return '';
        const baseId = resolveFieldId(field);
        return field.contactProperty ? `${baseId}.${field.contactProperty}` : baseId;
    };

    const orderedKeys = selectedFields.filter(Boolean).map(getFieldKey);

    // Quick map for lookups by rendered key
    const selectedFieldByKey = Object.fromEntries(
        selectedFields.filter(Boolean).map(f => [getFieldKey(f), f])
    );

    // Equalize row heights after data changes
    useEffect(() => {
        const equalizeRowHeights = () => {
            const table = document.querySelector('.report-table');
            if (!table) return;

            const rows = table.querySelectorAll('tbody tr');
            
            rows.forEach(row => {
                // Reset all cell heights first
                const cells = row.querySelectorAll('td');
                cells.forEach(cell => {
                    cell.style.height = 'auto';
                });

                // Get the tallest cell height in this row
                let maxHeight = 0;
                cells.forEach(cell => {
                    const cellHeight = cell.getBoundingClientRect().height;
                    if (cellHeight > maxHeight) {
                        maxHeight = cellHeight;
                    }
                });

                // Set all cells in this row to the max height
                cells.forEach(cell => {
                    cell.style.height = `${maxHeight}px`;
                    cell.style.verticalAlign = 'top'; // Ensure content aligns to top
                });
            });
        };

        // Run after DOM updates
        if (reportData && reportData.length > 0) {
            // Use requestAnimationFrame to ensure DOM is fully rendered
            requestAnimationFrame(() => {
                equalizeRowHeights();
            });
        }
    }, [reportData, selectedFields]); // Re-run when data or fields change

    const getFieldName = (key) => {
        // Check for blank columns first
        const selectedField = selectedFieldByKey[key];
        if (selectedField?.isBlank) {
            return selectedField.name;
        }
        if (key === 'contacts.full_name' || selectedField?.id === 'contacts.full_name' || selectedField?.name === 'contacts.full_name') {
            return 'Contact';
        }
        if (!customFields || !Array.isArray(customFields)) return key;
        // Extract actual field ID (handle nested structure)
        const actualFieldId = resolveFieldId(selectedField ?? key);
        const match = customFields.find(field => String(field.id) === String(actualFieldId));
        if (!match) return selectedField?.name || (key === 'contacts.full_name' ? 'Contact' : key);
        
        // Add section name for add_item fields and total fields, just like in ReportColumns
        const baseName = selectedField?.name || match.name;
        const shouldShowSection = (Number(match.add_item) === 1 || match.computed_type === "total_section");
        
        let displayName = shouldShowSection && match.section_name 
            ? `${baseName} (${match.section_name})`
            : baseName;
        
        // For contact fields with specific properties, show "FieldName (Property)" format
        if (selectedField?.contactProperty) {
            const propertyLabel = selectedField.contactProperty
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
            
            // Show "Client (Property)" to keep context of which field it is
            if (selectedField.contactProperty !== 'full_name') {
                displayName = `${baseName} (${propertyLabel})`;
            } else {
                displayName = baseName;
            }
        }

        if (key === 'contacts.full_name' || displayName === 'contacts.full_name') {
            return 'Contact';
        }
        
        // Add dropdown return type suffix if specified
        if (selectedField?.dropdownReturn) {
            const returnLabel = selectedField.dropdownReturn === 'id' ? 'ID' : 'Label';
            displayName = `${displayName} (${returnLabel})`;
        }
        
        return displayName;
    };

    const getFieldType = (key) => {
        // Blank columns have no field type
        const selectedField = selectedFieldByKey[key];
        if (selectedField?.isBlank) {
            return null;
        }
        if (!customFields || !Array.isArray(customFields)) return null;
        // Extract actual field ID (handle nested structure)
        const actualFieldId = resolveFieldId(selectedField ?? key);
        const match = customFields.find(field => String(field.id) === String(actualFieldId));
        
        // Debug field 265 specifically
        if (key === '265') {
            console.log('Field 265 debug:', {
                key,
                match,
                field_id: match?.field_id,
                customFields: customFields.slice(0, 3) // Show first 3 fields for structure
            });
        }
        
        return match ? match.field_id : null;
    };

    // Calculate optimal column widths based on content
    const getColumnWidth = (key) => {
        if (!reportData || !reportData.length) return '120px';
        
        const fieldName = getFieldName(key);
        const fieldType = getFieldType(key);
        
        // Get selected field for contact property and dropdown return
        const selectedField = selectedFieldByKey[key];
        
        // Get max content length for this column
        const maxContentLength = Math.max(
            fieldName.length,
            ...reportData.map(row => {
                let value = row[key];
                
                const rawFallback = row['_rawData']?.[key];
                if ((value === null || value === undefined || value === '') && rawFallback !== undefined) {
                    value = rawFallback;
                }
                
                // Extract contact property if specified
                if (selectedField?.contactProperty && value && typeof value === 'object') {
                    value = value[selectedField.contactProperty];
                    
                    // Format booleans as text
                    if (typeof value === 'boolean' || (value === 0 || value === 1)) {
                        value = value ? 'True' : 'False';
                    }
                }
                
                const formattedValue = formatValue(value, fieldType);
                return String(formattedValue || '').length;
            })
        );
        
        // Base width calculation with more generous spacing
        // Use 10px per character for better spacing, and 50px padding for breathing room
        let width = Math.max(80, Math.min(450, maxContentLength * 10 + 50));
        
        // For very long content (>40 characters), be even more generous
        if (maxContentLength > 40) {
            width = Math.max(width, maxContentLength * 11 + 60);
            width = Math.min(width, 500); // Cap at 500px for very long content
        }
        
        // Adjust for specific field types
        if (fieldType === 3) width = Math.max(width, 120); // Currency
        if (fieldType === 4) width = Math.max(width, 80);  // Percentage
        if (fieldType === 6) width = Math.max(width, 100); // Date
        
        // Special handling for case names and long text
        if (key === 'cases.case_name' || fieldName.toLowerCase().includes('name')) {
            width = Math.max(width, 200);
        }
        
        return `${width}px`;
    };

    // Get text alignment based on field type
    const getTextAlign = (key) => {
        const fieldType = getFieldType(key);
        // Right-align: currency (3), percentage (4), total_data_ident (17), calculation (19)
        if (fieldType === 3 || fieldType === 4 || fieldType === 17) return 'right';
        if (fieldType === 6) return 'center'; // Date
        return 'left';
    };

    const formatValue = (value, fieldType) => {
        if (value === null || value === undefined || value === '') return '';

        if (Array.isArray(value)) {
            return value.map((item) => {
                if (item === null || item === undefined || item === '') return '';
                if (typeof item !== 'object') return String(item);
                return String(item.file_name ?? item.name ?? item.label ?? item.display ?? item.value ?? 'Attached file');
            }).filter(Boolean).join(', ');
        }

        if (typeof value === 'object') {
            return String(value.display ?? value.file_name ?? value.name ?? value.label ?? value.value ?? 'Structured value');
        }
        
        const stringValue = String(value);
        
        // Currency fields - backend now formats these properly, just return as-is if already formatted
        // Types: 3 (currency), 17 (calculation)
        if (fieldType === 3 || fieldType === 17 || stringValue.startsWith('$')) {
            return stringValue;
        }
        
        // Percentage formatting (field_id 4)
        if (fieldType === 4) {
            const numValue = parseFloat(stringValue.replace(/[^0-9.-]/g, ''));
            if (!isNaN(numValue)) {
                return `${numValue}%`;
            }
        }
        
        // Date formatting (field_id 6)
        if (fieldType === 6) {
            if (stringValue && /^\d{4}-\d{2}-\d{2}/.test(stringValue)) {
                try {
                    // Parse date without timezone conversion by treating as local date
                    const [year, month, day] = stringValue.split('-').map(Number);
                    const date = new Date(year, month - 1, day);
                    return date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    });
                } catch {
                    return stringValue;
                }
            }
        }
        
        // Handle general date fields
        if (stringValue.includes('-') && stringValue.length >= 8 && /^\d{4}-\d{2}-\d{2}/.test(stringValue)) {
            try {
                // Parse date without timezone conversion by treating as local date
                const [year, month, day] = stringValue.split('-').map(Number);
                const date = new Date(year, month - 1, day);
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                });
            } catch {
                return stringValue;
            }
        }
        
        // Default: clean up comma spacing
        return stringValue.replace(/,\s*/g, ', ');
    };

    const getLookupLabel = (map, value) => {
        if (!map || value === null || value === undefined || value === '') return '';
        const key = String(value);
        return map[key] ?? map[value] ?? '';
    };

    const getLookupMapForKey = (dataKey) => {
        if (!dataKey) return null;
        if (/^cases\.team\[/.test(dataKey)) return lookupMaps.users;
        if (dataKey === 'cases.phase_id') return lookupMaps.phases;
        if (dataKey === 'cases.subphase_id') return lookupMaps.subphases;
        if (dataKey === 'cases.template_id') return lookupMaps.templates;
        if (dataKey === 'leads.case_type_id') return lookupMaps.caseTypes;
        if (dataKey === 'leads.marketing_source') return lookupMaps.marketingSources;
        if (dataKey === 'leads.assigned_to' || dataKey === 'leads.assigned_to_attorney' || dataKey === 'leads.intake_by') return lookupMaps.users;
        if (dataKey === 'leads.referral_contact' || dataKey === 'leads.db_origination' || dataKey === 'leads.referred_to' || dataKey === 'cases.contact_id' || dataKey === 'leads.contact_id') return lookupMaps.contacts;
        return null;
    };

    const handleSort = (key) => {
        if (onSort) {
            onSort(key);
        }
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <ChevronsUpDown size={14} />;
        return sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
    };

    // Calculate total table width needed
    const getTotalTableWidth = () => {
        if (!orderedKeys.length) return 'auto';
        
        const totalWidth = orderedKeys.reduce((sum, key) => {
            const width = parseInt(getColumnWidth(key));
            return sum + width;
        }, 0);
        
        // Only use calculated width, let CSS container handle minimum width
        return `${totalWidth}px`;
    };

    return (
        <div className="report-view">
            {fetchingReport ? (
                <RefreshCw size={16} className='spin' />
            ) : (
                reportData?.length > 0 ? (
                    <div className="report-table-container">
                        <table className="report-table" style={{ tableLayout: 'fixed', width: getTotalTableWidth() }}>
                            <thead>
                                <tr>
                                    {orderedKeys.map(key => (
                                        <th 
                                            key={key} 
                                            onClick={() => handleSort(key)}
                                            className="sortable-header"
                                            style={{
                                                width: getColumnWidth(key),
                                                textAlign: getTextAlign(key),
                                                minWidth: getColumnWidth(key)
                                            }}
                                        >
                                            <div className="header-content" style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: getTextAlign(key) === 'right' ? 'flex-end' : 
                                                              getTextAlign(key) === 'center' ? 'center' : 'flex-start'
                                            }}>
                                                <span>{getFieldName(key)}</span>
                                                {getSortIcon(key)}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.map((row, rowIndex) => (
                                    <tr key={rowIndex}>
                                        {orderedKeys.map(key => {
                                            // Check if this is a blank column
                                            const selectedField = selectedFieldByKey[key];
                                            if (selectedField?.isBlank) {
                                                return (
                                                    <td 
                                                        key={key}
                                                        style={{
                                                            textAlign: getTextAlign(key),
                                                            width: getColumnWidth(key),
                                                            maxWidth: getColumnWidth(key)
                                                        }}
                                                    >
                                                        {/* Empty cell for blank columns */}
                                                    </td>
                                                );
                                            }

                                            // Get base field ID for data lookup (backend returns data with base ID, not enhanced key)
                                            const dataKey = resolveFieldId(selectedField ?? key);

                                            let rawVal = row[dataKey];
                                            const rawDataValue = row['_rawData']?.[dataKey];

                                            if ((rawVal === null || rawVal === undefined || rawVal === '') && rawDataValue !== undefined) {
                                                rawVal = rawDataValue;
                                            }

                                            const wantsId = selectedField?.dropdownReturn === 'id';
                                            const wantsLabel = !selectedField?.dropdownReturn || selectedField?.dropdownReturn === 'label';

                                            if (wantsLabel) {
                                                const lookupMap = getLookupMapForKey(dataKey);
                                                if (lookupMap) {
                                                    const label = getLookupLabel(lookupMap, rawDataValue ?? rawVal);
                                                    if (label) {
                                                        rawVal = label;
                                                    }
                                                }
                                            } else if (wantsId) {
                                                if (rawDataValue !== undefined) {
                                                    rawVal = rawDataValue;
                                                }
                                            }
                                            
                                            // Extract contact property if specified
                                            if (selectedField?.contactProperty && rawVal && typeof rawVal === 'object') {
                                                rawVal = rawVal[selectedField.contactProperty];
                                                
                                                // Format booleans as text
                                                if (typeof rawVal === 'boolean' || (rawVal === 0 || rawVal === 1)) {
                                                    rawVal = rawVal ? 'True' : 'False';
                                                }
                                            }
                                            
                                            const fieldType = getFieldType(key);
                                            const displayVal = formatValue(rawVal, fieldType);
                                            
                                            // Use cases.id for navigation routing
                                            const linkCaseId = row['cases.id'] || row['case_id'] || row['id'];
                                            
                                            const isCaseIdCol = key === 'cases.id';
                                            const isCaseNameCol = key === 'cases.case_name';
                                            const shouldLink = (isCaseIdCol || isCaseNameCol) && linkCaseId;
                                            return (
                                                <td 
                                                    key={key}
                                                    style={{
                                                        textAlign: getTextAlign(key),
                                                        width: getColumnWidth(key),
                                                        maxWidth: getColumnWidth(key),
                                                        textWrap: 'wrap',
                                                        lineHeight: '1'
                                                    }}
                                                    title={displayVal && displayVal.length > 30 ? displayVal : undefined}
                                                >
                                                    {shouldLink ? (
                                                        <a href={`/case/${linkCaseId}`} className='case-link'>
                                                            {displayVal}
                                                        </a>
                                                    ) : displayVal}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p>No report data available.</p>
                )
            )}
        </div>
    );
};
